import { createFileRoute } from "@tanstack/react-router"
import { AnimatePresence, motion } from "motion/react"
import { type ReactNode, useCallback, useEffect, useState } from "react"
import { PageVignette } from "@/components/ui/page-vignette"
import { SectionTitle } from "@/components/ui/section-title"
import { StarField } from "@/components/ui/star-field"
import {
  FRAME_COUNT,
  FRAMES,
  type Frame,
  type Orient,
  RAW_FRAMES,
} from "@/data/gallery"
import { cn } from "@/lib/utils"

const GALLERY_URL = "https://eklo-seven.vercel.app/gallery"
const GALLERY_OG_IMAGE =
  "https://70fy8xbp6b.ufs.sh/f/BzM3qquY5hLZ00at04BSMAphtaFEswykqO5ZebimTCLnjfY1"
const GALLERY_DESCRIPTION =
  "Behind-the-scenes from the EKLO-Verse. Set photography, on-location stills, and raw clips from the production."

export const Route = createFileRoute("/gallery")({
  head: () => ({
    meta: [
      { title: "Gallery | EKLO-Verse" },
      { name: "description", content: GALLERY_DESCRIPTION },
      { property: "og:title", content: "Gallery | EKLO-Verse" },
      { property: "og:description", content: GALLERY_DESCRIPTION },
      { property: "og:url", content: GALLERY_URL },
      { property: "og:image", content: GALLERY_OG_IMAGE },
      { property: "og:image:alt", content: "EKLO-Verse — set panorama" },
      { name: "twitter:title", content: "Gallery | EKLO-Verse" },
      { name: "twitter:description", content: GALLERY_DESCRIPTION },
      { name: "twitter:image", content: GALLERY_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: GALLERY_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(buildGalleryLd()),
      },
    ],
  }),
  component: GalleryPage,
})

function buildGalleryLd() {
  return {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "@id": `${GALLERY_URL}#stills`,
    name: "EKLO-Verse — Behind-the-Scenes Stills",
    description: GALLERY_DESCRIPTION,
    url: GALLERY_URL,
    image: RAW_FRAMES.map((f) => ({
      "@type": "ImageObject",
      contentUrl: f.src,
      description: f.alt,
    })),
  }
}

const ASPECT_MOBILE: Record<Orient, string> = {
  L: "3 / 2",
  P: "3 / 4",
  Pano: "16 / 9",
}

const TICK_POSITIONS = [
  "top-1.5 left-1.5 border-t border-l",
  "top-1.5 right-1.5 border-t border-r",
  "bottom-1.5 left-1.5 border-b border-l",
  "bottom-1.5 right-1.5 border-b border-r",
] as const

const HOVER_FX =
  "shadow-[0_8px_30px_rgba(0,0,0,0.55)] transition-[border-color,box-shadow] duration-500 ease-out hover:border-brand/55 hover:shadow-[0_0_50px_rgb(var(--brand-rgb)/0.4)]"

// ─── Helpers ──────────────────────────────────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, "0")
const wrap = (i: number, n: number) => ((i % n) + n) % n

function useCarousel(count: number) {
  const [index, setIndex] = useState<number | null>(null)
  const close = useCallback(() => setIndex(null), [])
  const step = useCallback(
    (delta: number) =>
      setIndex((i) => (i === null ? null : wrap(i + delta, count))),
    [count]
  )

  useEffect(() => {
    if (index === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
      else if (e.key === "ArrowLeft") step(-1)
      else if (e.key === "ArrowRight") step(1)
    }
    window.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    return () => {
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [index, close, step])

  return { index, setIndex, close, step }
}

type Carousel = ReturnType<typeof useCarousel>

// ─── Page ─────────────────────────────────────────────────────────────────

function GalleryPage() {
  const frames = useCarousel(FRAME_COUNT)
  const open = frames.index !== null ? FRAMES[frames.index] : null

  return (
    <div className="fade-in relative animate-in overflow-x-hidden px-4 pt-36 pb-24 duration-500 sm:px-8 sm:pt-30 sm:pb-30">
      <StarField />
      <PageVignette />

      <div className="relative mx-auto max-w-350">
        <SectionIntro
          prefix="Frames in"
          accent="Orbit"
          description="Photos from the set. Some posed, most caught in passing."
          channel="CH·STILLS"
          count={FRAME_COUNT}
        />

        {/* Mobile: 2-col grid, portraits half width, landscapes/pano full */}
        <div className="mx-auto grid w-full max-w-md grid-flow-row-dense grid-cols-2 gap-3 lg:hidden">
          {FRAMES.map((f, i) => (
            <FrameCard
              key={f.src}
              frame={f}
              index={i}
              variant="mobile"
              onOpen={frames.setIndex}
            />
          ))}
        </div>

        {/* Desktop: 3-col grid, organic via rotation + float */}
        <div className="mx-auto hidden w-full max-w-265 grid-cols-3 gap-6 lg:grid xl:gap-8">
          {FRAMES.map((f, i) => (
            <FrameCard
              key={f.src}
              frame={f}
              index={i}
              variant="desktop"
              onOpen={frames.setIndex}
            />
          ))}
        </div>
      </div>

      <Lightbox
        carousel={frames}
        total={FRAME_COUNT}
        caption={open?.alt}
        ariaLabel={open?.alt}
      >
        {open && (
          <img
            src={open.href}
            alt={open.alt}
            width={open.orient === "P" ? 900 : open.orient === "Pano" ? 2400 : 1600}
            height={open.orient === "P" ? 1600 : open.orient === "Pano" ? 1200 : 900}
            className="max-h-[88vh] max-w-[90vw] border border-white/15 object-contain"
          />
        )}
      </Lightbox>
    </div>
  )
}

// ─── Section intro (title + description + counter) ────────────────────────

function SectionIntro({
  prefix,
  accent,
  description,
  channel,
  count,
}: {
  prefix: string
  accent: string
  description: string
  channel: string
  count: number
}) {
  return (
    <div className="mb-12 flex flex-col items-center text-center sm:mb-20">
      <SectionTitle
        prefix={prefix}
        accent={accent}
        className="mb-5 justify-center sm:mb-6"
      />
      <p className="max-w-[62ch] font-mono text-foreground text-sm leading-relaxed sm:text-base sm:leading-[1.7]">
        {description}
      </p>
      <p className="mt-5 font-mono text-[10px] text-muted-foreground uppercase tracking-[0.45em]">
        {channel} · {pad2(count)} TRANSMISSIONS
      </p>
    </div>
  )
}

function FrameCard({
  frame,
  index,
  variant,
  onOpen,
}: {
  frame: Frame
  index: number
  variant: "mobile" | "desktop"
  onOpen: (i: number) => void
}) {
  const isDesktop = variant === "desktop"
  const isPano = frame.orient === "Pano"

  const sizeClass = isDesktop
    ? isPano
      ? "col-span-2 aspect-[2/1]"
      : "aspect-square"
    : frame.orient === "P"
      ? "col-span-1"
      : "col-span-2"

  const inlineStyle = isDesktop
    ? {
        animation: `bts-float ${frame.dur}s ease-in-out ${frame.delay}s infinite`,
      }
    : {
        aspectRatio: ASPECT_MOBILE[frame.orient],
      }

  return (
    <button
      type="button"
      onClick={() => onOpen(index)}
      aria-label={`Open ${frame.alt}`}
      className={cn(
        "group relative block cursor-zoom-in overflow-hidden border border-white/15 bg-background",
        isDesktop && cn(HOVER_FX, "hover:z-30"),
        sizeClass
      )}
      style={inlineStyle}
    >
      <img
        src={frame.href}
        alt={frame.alt}
        width={frame.orient === "P" ? 900 : frame.orient === "Pano" ? 2400 : 1600}
        height={frame.orient === "P" ? 1600 : frame.orient === "Pano" ? 1200 : 900}
        className={cn(
          "h-full w-full object-cover",
          isDesktop
            ? "brightness-90 contrast-105 saturate-90 transition-[filter,scale] duration-500 group-hover:scale-[1.05] group-hover:brightness-110 group-hover:saturate-110"
            : "brightness-95 contrast-105"
        )}
        loading="lazy"
        decoding="async"
      />
      <span
        className={cn(
          "pointer-events-none absolute inset-0 bg-linear-to-b from-transparent via-transparent",
          isDesktop
            ? "to-black/55 transition-opacity duration-500 group-hover:opacity-30"
            : "to-black/65"
        )}
      />
      {isDesktop && (
        <span className="pointer-events-none absolute inset-0 ring-1 ring-brand/0 ring-inset transition-shadow duration-500 group-hover:ring-brand/35" />
      )}
      <span
        className={cn(
          "absolute font-mono text-[9px] text-muted-foreground uppercase",
          isDesktop
            ? "bottom-2 left-2 tracking-[0.45em]"
            : "bottom-3 left-3 tracking-[0.4em]"
        )}
      >
        {pad2(index + 1)} · BTS
      </span>
      <CornerTicks />
    </button>
  )
}

// ─── Lightbox ─────────────────────────────────────────────────────────────

function Lightbox({
  carousel,
  total,
  ariaLabel,
  caption,
  children,
}: {
  carousel: Carousel
  total: number
  ariaLabel?: string
  caption?: string
  children: ReactNode
}) {
  const { index, close, step } = carousel
  return (
    <AnimatePresence>
      {index !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-100 flex items-center justify-center bg-background/85 p-6 backdrop-blur-md sm:p-12"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
        >
          <ChevronButton dir="prev" onClick={() => step(-1)} />
          <ChevronButton dir="next" onClick={() => step(1)} />

          <motion.div
            key={index}
            initial={{ scale: 0.97, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.97, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-h-[88vh] max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
          >
            {children}
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="absolute -top-2 -right-2 flex size-9 items-center justify-center bg-brand text-lg text-white leading-none transition-colors hover:bg-brand/85 sm:-top-4 sm:-right-4"
            >
              ×
            </button>
            {caption && (
              <p className="absolute right-0 -bottom-9 left-0 text-center font-mono text-[10px] text-muted-foreground uppercase tracking-[0.4em]">
                {pad2(index + 1)} / {pad2(total)} · {caption}
              </p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// ─── Chrome ───────────────────────────────────────────────────────────────

function ChevronButton({
  dir,
  onClick,
}: {
  dir: "prev" | "next"
  onClick: () => void
}) {
  const isPrev = dir === "prev"
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      aria-label={isPrev ? "Previous" : "Next"}
      className={cn(
        "absolute top-1/2 z-10 flex size-11 -translate-y-1/2 items-center justify-center bg-brand text-white transition-colors hover:bg-brand/85 sm:size-12",
        isPrev ? "left-3 sm:left-6" : "right-3 sm:right-6"
      )}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="size-5"
        aria-hidden="true"
      >
        <title>{isPrev ? "Previous" : "Next"}</title>
        <path
          d={isPrev ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  )
}

function CornerTicks() {
  return (
    <>
      {TICK_POSITIONS.map((pos) => (
        <span
          key={pos}
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute size-2 border-white/45",
            pos
          )}
        />
      ))}
    </>
  )
}
